import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ServiceDetailsPanel = ({ serviceDetails, technician }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    })?.format(price);
  };

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Détails de l'intervention</h3>
            <p className="text-sm text-muted-foreground">Référence : {serviceDetails?.reference}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            serviceDetails?.urgency === 'urgent' ?'bg-error/10 text-error' 
              : serviceDetails?.urgency === 'today' ?'bg-warning/10 text-warning' :'bg-success/10 text-success'
          }`}>
            {serviceDetails?.urgency === 'urgent' ? 'Urgent' : 
             serviceDetails?.urgency === 'today'? 'Aujourd\'hui' : 'Planifié'}
          </span>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Date et heure</p>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDate(serviceDetails?.date)}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatTime(serviceDetails?.timeSlot)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="MapPin" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Adresse</p>
              <p className="text-sm text-muted-foreground mt-1">
                {serviceDetails?.address?.street}
              </p>
              <p className="text-sm text-muted-foreground">
                {serviceDetails?.address?.postalCode} {serviceDetails?.address?.city}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Wrench" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Services demandés</p>
              <ul className="mt-2 space-y-1">
                {serviceDetails?.services?.map((service, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {service?.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Votre technicien</h3>
        
        <div className="flex items-start gap-4 mb-4">
          <div className="relative">
            <Image
              src={technician?.photo}
              alt={technician?.photoAlt}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success rounded-full border-2 border-card" />
          </div>
          
          <div className="flex-1">
            <h4 className="text-base font-semibold text-foreground">{technician?.name}</h4>
            <p className="text-sm text-muted-foreground mt-1">{technician?.specialty}</p>
            <div className="flex items-center gap-1 mt-2">
              <Icon name="Star" size={14} className="text-warning fill-warning" />
              <span className="text-sm font-medium text-foreground">{technician?.rating}</span>
              <span className="text-xs text-muted-foreground">({technician?.reviews} avis)</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm font-medium">
            <Icon name="Phone" size={16} color="#FFFFFF" />
            Appeler
          </button>
          <button className="flex-1 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center justify-center gap-2 text-sm font-medium">
            <Icon name="MessageCircle" size={16} color="#FFFFFF" />
            Message
          </button>
        </div>
      </div>
      <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Récapitulatif des coûts</h3>
        
        <div className="space-y-3">
          {serviceDetails?.pricing?.items?.map((item, index) => (
            <div key={index} className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{item?.name}</p>
                {item?.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{item?.description}</p>
                )}
              </div>
              <span className="text-sm font-medium text-foreground ml-4">
                {formatPrice(item?.price)}
              </span>
            </div>
          ))}

          <div className="border-t border-border pt-3 mt-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Sous-total</span>
              <span className="text-sm font-medium text-foreground">
                {formatPrice(serviceDetails?.pricing?.subtotal)}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">
                TVA ({serviceDetails?.pricing?.vatRate}%)
              </span>
              <span className="text-sm font-medium text-foreground">
                {formatPrice(serviceDetails?.pricing?.vat)}
              </span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-border">
              <span className="text-base font-semibold text-foreground">Total</span>
              <span className="text-lg font-bold text-primary">
                {formatPrice(serviceDetails?.pricing?.total)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-primary/10 rounded-lg flex items-start gap-2">
          <Icon name="Shield" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-foreground">
            <span className="font-medium">Prix affiché — pas de frais cachés.</span> Toute variation sera expliquée et validée avant toute action.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsPanel;